import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../services/api';

// Task 28 — 360° değerlendirici seçimi + yönetici onayı + form doldurma.
// Gerçek rotalar (grep ile doğrulanmış):
//   GET  v1/evaluator-nominations/my?periodId[eq]&status[in]      (Task 11)
//   POST v1/evaluator-nominations/my                               (Task 11 — toplu öneri)
//   GET  v1/evaluator-nominations?periodId[eq]&status[in]          (Task 11 — yönetim;
//        Görev 6/PMB-09'dan beri KAPSAMLI: enterpriseAdmin tüm kiracıyı + harici
//        e-postayı, hat yöneticisi yalnız doğrudan raporlarını görür ve harici
//        e-posta null döner — ad-soyad kalır)
//   POST v1/evaluator-nominations/{id}/manager-decision {decision} (Task 11)
//   POST v1/evaluator-nominations/manager-add                      (Task 11)
//   GET  v1/feedback-responses/my-pending?periodId[eq]              (Task 28 — BE eklentisi)
//   GET  v1/feedback-responses/my/{id}/form                         (Task 28 — BE eklentisi)
//   POST v1/feedback-responses/my/{id}/fill {answers,overallLevelId}  (Task 12;
//     Plan 2 Task 8 — eski string overallLevel yerine FK)
// GET v1/evaluator-nominations(/my) EvaluatorNominationResponse'ta İSİM alanı
// YOK (yalnız EmployeeId/EvaluatorEmployeeId Guid) — bu dosyadaki
// useEmployeeNames lazy-cache hook'u satır bazında /v1/employees/{id} ile adı
// çözer (TalentPoolPage/GoalEditor'daki buildEmployeeAsync deseniyle aynı arama
// ucu, farklı kullanım: burada tekil id→ad çözümlemesi çoklu satır için).

export function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      // Task 31 inceleme bulgusu (Important #2): EmployeeController yalnız
      // firstName[like]/lastName[like]/fullName[like] bind ediyor —
      // `name[like]` hiçbir alana bağlanmıyordu (no-op, sessizce ilk 20 kaydı
      // döndürüyordu). `fullName[like]` ad+soyad birleşiminde arıyor.
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// Nomination'da izin verilen roller (ApplyNominationsAsync — peer|subordinate|senior).
// Görünüm bağlamlarında (my-pending, form) FeedbackResponse.EvaluatorRole ayrıca
// self/manager de içerebilir (EvaluationPeriodDataAccess.GenerateSelfManagerFeedbackResponsesAsync
// — aktivasyonda otomatik üretilir), bu yüzden i18n'de 5 rol de karşılanır.
export const NOMINATION_ROLES = ['peer', 'subordinate', 'senior'];

// P1 — HARİCİ (şirket dışı) değerlendirici rolleri: yalnız peer|senior
// (backend: performance.nomination.externalRoleInvalid — subordinate yasak).
export const EXTERNAL_NOMINATION_ROLES = ['peer', 'senior'];

// Basit istemci-taraflı e-posta ön kontrolü — gerçek kaynak sunucu doğrulaması
// (performance.nomination.externalEmailRequired); burada yalnız düğme/hint UX'i.
export const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());

// Harici satır etiketi: ad varsa "Ad (e-posta)", yoksa e-posta.
export const externalDisplayName = (n) => {
  const name = (n.externalFullName || '').trim();
  const email = (n.externalEmail || '').trim();
  return name ? `${name} (${email})` : email;
};

export const MIN_TOTAL_NOMINATIONS = 3;
export const MAX_TOTAL_NOMINATIONS = 10;

// Anonimlik bilgi şeridi YALNIZ peer/subordinate/senior formlarında gösterilir —
// self (kendi kendine) ve manager (zaten doğrudan raporunu değerlendiriyor,
// kimliği belli) formlarında anlamsız.
export const ANONYMOUS_EVALUATOR_ROLES = ['peer', 'subordinate', 'senior'];

const fetchPeriodsByStatus = async (status) => {
  const res = await api.get('/v1/evaluation-periods', { params: { 'status[eq]': status, pageSize: 50 } });
  const r = res.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

// Nomination/manager-add "active" veya "evaluation" durumundaki dönemlere karşı
// açık (LoadOpenPeriodAsync) — period-goals/config'teki fetchPeriodsByStatus
// çiftiyle birebir aynı desen (bağımsız kopya — farklı sayfa grubu).
export const useFeedbackPeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  const fetchPeriods = useCallback(async () => {
    setPeriodsFetching(true);
    try {
      const [active, evaluation] = await Promise.all([
        fetchPeriodsByStatus('active'),
        fetchPeriodsByStatus('evaluation'),
      ]);
      setPeriods([...active, ...evaluation]);
    } catch {
      setPeriods([]);
    } finally {
      setPeriodsFetching(false);
    }
  }, []);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  return { periods, periodsFetching };
};

// ── MyEvaluatorsPage ──────────────────────────────────────────────────────

export const useMyEvaluatorsApi = (periodId) => {
  const [nominations, setNominations] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!periodId) { setNominations([]); return; }
    setFetching(true);
    api.get('/v1/evaluator-nominations/my', { params: { 'periodId[eq]': periodId, pageSize: 50 } })
      .then((res) => {
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        setNominations(items);
      })
      .catch(() => setNominations([]))
      .finally(() => setFetching(false));
  }, [periodId, refreshTrigger]);

  // items: [{ evaluatorEmployeeId, evaluatorRole }]
  const propose = useCallback(async (targetPeriodId, items) => {
    try {
      await api.post('/v1/evaluator-nominations/my', { periodId: targetPeriodId, items });
      return true;
    } catch {
      return false;
    }
  }, []);

  return { nominations, fetching, refresh, propose };
};

// "Mevcut" = rejected HARİÇ (proposed+approved) — ApplyNominationsAsync'in
// min3/max10 sayımıyla AYNI tanım (bkz. backend yorumu: "TOPLAM öneri").
export const countActiveNominations = (nominations) =>
  (nominations || []).filter((n) => n.status !== 'rejected').length;

// ── EvaluatorApprovalPage ─────────────────────────────────────────────────

export const useEvaluatorApprovalApi = (queryParams) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const paramsKey = JSON.stringify(queryParams || {});

  useEffect(() => {
    setFetching(true);
    api.get('/v1/evaluator-nominations', { params: { ...queryParams, 'status[in]': 'proposed' } })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
    // paramsKey bilinçli bağımlılık (bkz. period-goals/config useApiPagedList'teki
    // aynı yorum) — params her render'da yeni referans olabilir, içerik
    // değişmediyse yeniden istek atılmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, refreshTrigger]);

  const decide = useCallback(async (id, decision) => {
    try {
      await api.post(`/v1/evaluator-nominations/${id}/manager-decision`, { decision });
      return true;
    } catch {
      return false;
    }
  }, []);

  const managerAdd = useCallback(async (payload) => {
    try {
      await api.post('/v1/evaluator-nominations/manager-add', payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { data, fetching, pagination, refresh, decide, managerAdd };
};

// Lazy id→ad çözümü — GET /v1/evaluator-nominations satırlarında yalnız Guid
// var (EvaluatorNominationResponse'ta isim alanı YOK), her benzersiz id için
// tek seferlik /v1/employees/{id} çağrısı yapılır ve sonuç sayfa ömrü boyunca
// önbelleğe alınır.
export const useEmployeeNames = (ids) => {
  const [names, setNames] = useState({});
  // Son önbelleği ref'te tutuyoruz — efekt içinde "hangi id'ler eksik" sorusunu
  // names'i dep listesine eklemeden (sonsuz döngü yaratmadan) cevaplayabilmek
  // için (bkz. useApiPagedList'teki paramsKey deseniyle aynı gerekçe).
  const namesRef = useRef(names);
  namesRef.current = names;
  const idsKey = JSON.stringify([...new Set((ids || []).filter(Boolean))].sort());

  useEffect(() => {
    const uniqueIds = [...new Set((ids || []).filter(Boolean))];
    const missing = uniqueIds.filter((id) => !(id in namesRef.current));
    if (missing.length === 0) return undefined;

    let cancelled = false;
    // allSettled — bir id'nin çözümü başarısız olsa bile (ör. silinmiş çalışan)
    // partideki diğer id'lerin adı yine de çözülür; başarısız olan ham id'ye
    // düşer (Promise.all kullansaydık TEK reddedilen id tüm partiyi bozardı).
    Promise.allSettled(missing.map((id) => employeeAsync.onGetById(id)))
      .then((results) => {
        if (cancelled) return;
        setNames((current) => {
          const next = { ...current };
          missing.forEach((id, i) => {
            const r = results[i];
            next[id] = (r.status === 'fulfilled' && r.value?.name) || id;
          });
          return next;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return names;
};

// ── FillFeedbackPage ──────────────────────────────────────────────────────

// queryParams: { pageNumber, pageSize } — GET /my-pending yalnız bu ikisini +
// periodId[eq]'yi destekliyor (bkz. FeedbackResponseController.GetMyPending),
// bu yüzden diğer sayfalardaki genel parseTableParams çıktısından yalnız
// bunlar kullanılır (sort/filter yok — DataTable'da o kolonlar filter:false).
export const useMyPendingFeedbackApi = (queryParams) => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const paramsKey = JSON.stringify(queryParams || {});

  useEffect(() => {
    setFetching(true);
    api.get('/v1/feedback-responses/my-pending', { params: queryParams })
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setData([]))
      .finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, refreshTrigger]);

  return { data, fetching, pagination, refresh };
};

export const useFeedbackFormApi = () => {
  const getForm = useCallback(async (responseId) => {
    try {
      const res = await api.get(`/v1/feedback-responses/my/${responseId}/form`);
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  // payload: { answers: [{ questionId, likertValue?, textValue? }], overallLevelId? }
  const submitFill = useCallback(async (responseId, payload) => {
    try {
      await api.post(`/v1/feedback-responses/my/${responseId}/fill`, payload);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { getForm, submitFill };
};

// ── AI değerlendirme taslağı (P2 Faz 1 / Paket A) ─────────────────────────
//   POST v1/performance/ai/feedback-draft {periodId, employeeId}
//     → {strengths, developmentAreas, notableAchievement, generalComment, evidenceThin}
// Uç HİÇBİR mutasyon yapmaz — dönen metin yalnız formu ön-doldurur, gönderim
// yine yöneticinin kendi "Gönder" tıklamasıyla olur. Hata yolunda null döner
// (toast SADECE Axios interceptor'ında — bileşende catch/toast YOK), böylece
// çağıran form verisini korur ve düğme yeniden denenebilir kalır.
export const usePerformanceAiApi = () => {
  const draftFeedback = useCallback(async (periodId, employeeId) => {
    try {
      // Gemini çağrısı + kanıt toplama, api.js'teki 30 sn'lik genel timeout'u
      // yoğun dönemde aşabiliyor; istek başına 90 sn veriyoruz (backend zaten
      // sağlayıcı hatasında 502 "performance.ai.unavailable" döner, o yolda
      // istek erken biter).
      const res = await api.post(
        '/v1/performance/ai/feedback-draft',
        { periodId, employeeId },
        { timeout: 90000 },
      );
      return res.data?.data || null;
    } catch {
      return null;
    }
  }, []);

  // Öz değerlendirme taslağı (POST .../self-review-draft) müşteri geri bildirimiyle (2026-10-28) uçtan uca kaldırıldı.

  // P7a (analiz §33 M72/M73) — taslak kullanım sonucu ve AI'sız hazırlık süresi. Ölçüm hatası formu ETKİLEMEZ.
  const recordOutcome = useCallback(async (draftId, outcome, activeSeconds) => {
    try {
      await api.post(`/v1/performance/ai/drafts/${draftId}/outcome`, { outcome, activeSeconds });
    } catch { /* yakalayıcı gösterir; form akışı sürer */ }
  }, []);

  const recordPrepSession = useCallback(async (kind, periodId, activeSeconds) => {
    if (!activeSeconds || activeSeconds < 1) return;
    try {
      await api.post('/v1/performance/ai/prep-sessions', { kind, periodId, activeSeconds });
    } catch { /* yakalayıcı gösterir; form akışı sürer */ }
  }, []);

  return { draftFeedback, recordOutcome, recordPrepSession };
};
